<template>
  <div class="stock-page">
    <header class="stock-page__header">
      <div>
        <h1>{{ $t('catalog.stock.title') }}</h1>
        <p>{{ $t('catalog.stock.subtitle') }}</p>
      </div>
      <v-text-field
        v-model="search"
        outlined
        dense
        hide-details
        clearable
        prepend-inner-icon="mdi-magnify"
        :placeholder="$t('catalog.common.searchByName')"
        class="stock-page__search"
      />
    </header>

    <section class="stock-page__section">
      <div class="stock-page__section-title">{{ $t('catalog.common.categories') }}</div>
      <div class="stock-page__chips">
        <button
          v-for="category in categories"
          :key="category.id"
          type="button"
          class="stock-page__chip"
          :class="{ 'stock-page__chip--active': categoryId === category.id && !search }"
          @click="selectCategory(category.id)"
        >
          {{ category.name }}
        </button>
      </div>
    </section>

    <div v-if="loading" class="stock-page__loader">
      <v-progress-circular indeterminate color="#0b4f8a" size="44" width="3" />
      <span>{{ $t('catalog.common.loadingProducts') }}</span>
    </div>

    <section v-else-if="products.length" class="stock-page__grid">
      <article v-for="product in products" :key="product.id" class="stock-card">
        <div class="stock-card__top">
          <div class="stock-card__badge">{{ getCountTypeLabel(product.count_type) }}</div>
          <div class="stock-card__actions">
            <button
              type="button"
              class="stock-card__icon-btn"
              :title="$t('catalog.stock.history')"
              @click="openHistory(product)"
            >
              <i class="mdi mdi-history"></i>
            </button>
            <button
              type="button"
              class="stock-card__icon-btn stock-card__icon-btn--waste"
              :title="$t('catalog.stock.waste')"
              @click="openWaste(product)"
            >
              <i class="mdi mdi-trash-can-outline"></i>
            </button>
            <button
              type="button"
              class="stock-card__icon-btn stock-card__icon-btn--accent"
              :title="$t('catalog.stock.replenish')"
              @click="openCount(product)"
            >
              <i class="mdi mdi-plus"></i>
            </button>
          </div>
        </div>

        <h3>{{ product.name }}</h3>

        <div class="stock-card__meta">
          <div>
            <span>{{ $t('catalog.common.cost') }}</span>
            <strong>{{ formatMoney(product.self_price) }}</strong>
          </div>
          <div>
            <span>{{ $t('catalog.stock.retail') }}</span>
            <strong>{{ formatMoney(product.price) }}</strong>
          </div>
          <div>
            <span>{{ $t('catalog.common.stock') }}</span>
            <strong class="stock-card__qty">
              {{ product.quantity }} {{ getCountTypeLabel(product.count_type) }}
            </strong>
          </div>
        </div>
      </article>
    </section>

    <div v-else class="stock-page__empty">
      <i class="mdi mdi-warehouse"></i>
      <p>{{ emptyTitle }}</p>
      <span>{{ emptyText }}</span>
    </div>

    <div v-if="!loading && pageCount > 1" class="stock-page__pagination">
      <v-pagination
        v-model="page"
        :length="pageCount"
        :total-visible="5"
        color="#0b4f8a"
      />
    </div>

    <v-dialog width="520" v-model="historyOpen">
      <v-card class="stock-page__dialog">
        <v-card-title>{{ $t('catalog.stock.historyTitle', { name: historyProductName }) }}</v-card-title>
        <v-card-text>
          <div v-if="historyDates.length">
            <div v-for="date in historyDates" :key="date" class="stock-history">
              <p class="stock-history__date">{{ date }}</p>
              <p v-if="daySoldQty(date)">
                {{ $t('catalog.stock.sold') }}
                <b>{{ formatQty(daySoldQty(date)) }} {{ historyUnit }}</b>
              </p>
              <p v-if="daySoldQty(date)">
                {{ $t('catalog.stock.profit') }}
                <b>{{ formatMoney(daySoldProfit(date)) }}</b>
              </p>
              <p v-if="dayWasteQty(date)" class="stock-history__waste">
                {{ $t('catalog.stock.wasteLabel') }}
                <b>{{ formatQty(dayWasteQty(date)) }} {{ historyUnit }}</b>
              </p>
              <p v-if="dayWasteQty(date)" class="stock-history__waste">
                {{ $t('catalog.stock.wasteCost') }}
                <b>{{ formatMoney(dayWasteCost(date)) }}</b>
              </p>
            </div>
          </div>
          <div v-else class="stock-history__empty">{{ $t('catalog.stock.historyEmpty') }}</div>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="historyOpen = false">{{ $t('catalog.common.close') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog width="480" v-model="countOpen">
      <v-card class="stock-page__dialog">
        <v-card-title>{{ $t('catalog.stock.replenishTitle', { name: countProductName }) }}</v-card-title>
        <v-card-text>
          <v-text-field v-model="countForm.count" :label="$t('catalog.common.quantity')" outlined dense type="tel" />
          <v-text-field v-model="countForm.self_price" :label="$t('catalog.common.cost')" outlined dense type="tel" />
          <v-text-field v-model="countForm.price" :label="$t('catalog.common.retailPrice')" outlined dense type="tel" />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="countOpen = false">{{ $t('catalog.common.cancel') }}</v-btn>
          <v-btn color="#0b4f8a" dark :loading="saving" @click="saveCount">{{ $t('catalog.common.save') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog width="480" v-model="wasteOpen">
      <v-card class="stock-page__dialog">
        <v-card-title>{{ $t('catalog.stock.wasteTitle', { name: wasteProductName }) }}</v-card-title>
        <v-card-text>
          <p class="stock-waste__hint">
            {{ $t('catalog.stock.wasteHint', { stock: formatQty(wasteStock), unit: wasteUnit }) }}
          </p>
          <v-text-field
            v-model="wasteForm.count"
            :label="$t('catalog.stock.wasteQty', { unit: wasteUnit })"
            outlined
            dense
            type="number"
            min="0"
            step="any"
            inputmode="decimal"
            data-touch-decimal="1"
          />
          <v-text-field
            v-model="wasteForm.note"
            :label="$t('catalog.stock.wasteNote')"
            outlined
            dense
            clearable
          />
          <div class="stock-waste__sum">
            <span>{{ $t('catalog.stock.wasteCostPreview') }}</span>
            <strong>{{ formatMoney(wasteCostPreview) }}</strong>
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="wasteOpen = false">{{ $t('catalog.common.cancel') }}</v-btn>
          <v-btn color="#b42318" dark :loading="savingWaste" @click="saveWaste">
            {{ $t('catalog.stock.wasteSubmit') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { debounce } from 'lodash'
import moment from 'moment'
import { getCategories } from '@/api/categories'
import { getProducts, updateProductCount, writeOffProductWaste } from '@/api/products'
import { formatMoney, getCountTypeLabel } from '@/utils/format'
import { notify, notifyError } from '@/utils/notify'

function isWasteLog(log) {
  return log && log.type === 'waste'
}

export default {
  name: 'StockPage',
  data() {
    return {
      search: '',
      page: 1,
      pageCount: 0,
      categories: [],
      products: [],
      categoryId: null,
      historyOpen: false,
      historyLogs: {},
      historyProductName: '',
      historyUnit: '',
      countOpen: false,
      productId: null,
      countProductName: '',
      countForm: {
        count: '',
        self_price: '',
        price: '',
      },
      wasteOpen: false,
      wasteProductId: null,
      wasteProductName: '',
      wasteStock: 0,
      wasteUnitCost: 0,
      wasteUnit: '',
      wasteForm: {
        count: '',
        note: '',
      },
      saving: false,
      savingWaste: false,
      loading: false,
      formatMoney,
      getCountTypeLabel,
    }
  },
  computed: {
    historyDates() {
      return Object.keys(this.historyLogs)
    },
    wasteCostPreview() {
      const qty = Number(this.wasteForm.count) || 0
      return Math.round(qty * (Number(this.wasteUnitCost) || 0) * 100) / 100
    },
    emptyTitle() {
      if (!this.categories.length) return this.$t('catalog.stock.noCategories')
      if (this.search) return this.$t('catalog.stock.nothingFound')
      return this.$t('catalog.stock.noProducts')
    },
    emptyText() {
      if (!this.categories.length) return this.$t('catalog.stock.noCategoriesHint')
      if (this.search) return this.$t('catalog.stock.searchHint')
      return this.$t('catalog.stock.noProductsHint')
    },
  },
  mounted() {
    this.loadCategories()
  },
  watch: {
    page() {
      this.loadProducts()
    },
    search() {
      this.debouncedSearch()
    },
  },
  methods: {
    sumField(items, field) {
      return (items || []).reduce((sum, item) => sum + Number(item[field] || 0), 0)
    },
    async loadCategories() {
      try {
        this.categories = await getCategories()
        if (this.categories.length) {
          this.selectCategory(this.categories[0].id)
        }
      } catch (error) {
        notifyError(error)
      }
    },
    async loadProducts() {
      if (!this.categoryId && !this.search) {
        this.products = []
        this.pageCount = 0
        this.loading = false
        return
      }

      this.loading = true
      try {
        const params = {
          page: this.page,
          stock: 'stock',
        }

        if (this.search) {
          params.search = this.search
        } else {
          params.category_id = this.categoryId
        }

        const data = await getProducts(params)
        this.products = data.data || []
        this.pageCount = Number(data.last_page) || 0

        if (this.page > this.pageCount && this.pageCount > 0) {
          this.page = this.pageCount
        }
      } catch (error) {
        notifyError(error)
      } finally {
        this.loading = false
      }
    },
    selectCategory(id) {
      if (this.categoryId === id && !this.search) return
      this.categoryId = id
      this.search = ''
      this.products = []
      this.resetToFirstPage()
    },
    resetToFirstPage() {
      if (this.page !== 1) {
        this.page = 1
      } else {
        this.loadProducts()
      }
    },
    debouncedSearch: debounce(function () {
      this.resetToFirstPage()
    }, 500),
    formatQty(value) {
      const number = Number(value) || 0
      if (Math.abs(number - Math.round(number)) < 1e-9) return String(Math.round(number))
      return String(Math.round(number * 1000) / 1000)
    },
    daySoldLogs(date) {
      return (this.historyLogs[date] || []).filter((log) => !isWasteLog(log))
    },
    dayWasteLogs(date) {
      return (this.historyLogs[date] || []).filter((log) => isWasteLog(log))
    },
    daySoldQty(date) {
      return Math.abs(this.sumField(this.daySoldLogs(date), 'quantity'))
    },
    daySoldProfit(date) {
      const logs = this.daySoldLogs(date)
      return this.sumField(logs, 'price') - this.sumField(logs, 'self_price')
    },
    dayWasteQty(date) {
      return Math.abs(this.sumField(this.dayWasteLogs(date), 'quantity'))
    },
    dayWasteCost(date) {
      return this.sumField(this.dayWasteLogs(date), 'self_price')
    },
    openHistory(product) {
      const logs = product.product_logs || []
      this.historyProductName = product.name
      this.historyUnit = getCountTypeLabel(product.count_type)
      this.historyLogs = logs.reduce((acc, log) => {
        if (!log || log.quantity === undefined || !log.created_at || Number(log.quantity) >= 0) {
          return acc
        }
        const date = moment(log.created_at).format('YYYY-MM-DD')
        if (!acc[date]) acc[date] = []
        acc[date].push(log)
        return acc
      }, {})
      this.historyOpen = true
    },
    openCount(product) {
      this.productId = product.id
      this.countProductName = product.name
      this.countForm = {
        count: '',
        self_price: product.self_price || '',
        price: product.price || '',
      }
      this.countOpen = true
    },
    openWaste(product) {
      this.wasteProductId = product.id
      this.wasteProductName = product.name
      this.wasteStock = Number(product.quantity) || 0
      this.wasteUnitCost = Number(product.self_price) || 0
      this.wasteUnit = getCountTypeLabel(product.count_type)
      this.wasteForm = { count: '', note: '' }
      this.wasteOpen = true
    },
    async saveCount() {
      if (!this.countForm.count) {
        notify(this.$t('catalog.stock.enterQuantity'), 'warning')
        return
      }
      if (!this.countForm.price || !this.countForm.self_price) {
        notify(this.$t('catalog.stock.enterPriceAndCost'), 'warning')
        return
      }

      this.saving = true
      try {
        await updateProductCount({
          ...this.countForm,
          product_id: this.productId,
        })
        notify(this.$t('catalog.stock.stockUpdated'))
        this.countOpen = false
        await this.loadProducts()
      } catch (error) {
        notifyError(error)
      } finally {
        this.saving = false
      }
    },
    async saveWaste() {
      const count = Number(this.wasteForm.count)
      if (!count || count <= 0) {
        notify(this.$t('catalog.stock.enterQuantity'), 'warning')
        return
      }
      if (count > this.wasteStock) {
        notify(this.$t('catalog.stock.wasteTooMuch', { stock: this.formatQty(this.wasteStock) }), 'warning')
        return
      }

      this.savingWaste = true
      try {
        const result = await writeOffProductWaste({
          product_id: this.wasteProductId,
          count,
          note: this.wasteForm.note || '',
        })
        notify(result.message || this.$t('catalog.stock.wasteSaved'))
        this.wasteOpen = false
        await this.loadProducts()
      } catch (error) {
        notifyError(error)
      } finally {
        this.savingWaste = false
      }
    },
  },
}
</script>

<style scoped lang="scss">
.stock-page {
  --ink: #152033;
  --muted: #6b7a90;
  --accent: #0b4f8a;
  --line: #d9e2ec;

  padding: 28px 32px 40px;
  color: var(--ink);
}

.stock-page__header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
  margin-bottom: 24px;

  h1 {
    margin: 0;
    font-size: 28px;
    font-weight: 750;
    letter-spacing: -0.02em;
  }

  p {
    margin: 6px 0 0;
    color: var(--muted);
    font-size: 14px;
  }
}

.stock-page__search {
  width: 320px;
  max-width: 100%;
  background: #fff;
  border-radius: 12px;
}

.stock-page__section {
  margin-bottom: 22px;
}

.stock-page__section-title {
  margin-bottom: 10px;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--muted);
}

.stock-page__chips {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.stock-page__chip {
  padding: 8px 14px;
  border: 1px solid var(--line);
  border-radius: 999px;
  background: #fff;
  color: var(--ink);
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    border-color: #9eb6cc;
  }

  &--active {
    background: var(--accent);
    border-color: var(--accent);
    color: #fff;
  }
}

.stock-page__loader {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 14px;
  min-height: 240px;
  padding: 40px 20px;
  border: 1px dashed var(--line);
  border-radius: 16px;
  background: #fff;
  color: var(--muted);
  font-size: 14px;
  font-weight: 600;
}

.stock-page__grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
  gap: 14px;
}

.stock-card {
  display: flex;
  flex-direction: column;
  gap: 14px;
  padding: 16px;
  background: #fff;
  border: 1px solid var(--line);
  border-radius: 16px;
  transition: border-color 0.2s ease, transform 0.15s ease;

  &:hover {
    border-color: #b7c6d6;
    transform: translateY(-2px);
  }

  h3 {
    margin: 0;
    font-size: 17px;
    font-weight: 700;
    line-height: 1.3;
  }
}

.stock-card__top {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 10px;
}

.stock-card__badge {
  padding: 4px 8px;
  border-radius: 8px;
  background: #eef4fa;
  color: var(--accent);
  font-size: 12px;
  font-weight: 700;
}

.stock-card__actions {
  display: flex;
  gap: 6px;
}

.stock-card__icon-btn {
  width: 34px;
  height: 34px;
  border: 1px solid var(--line);
  border-radius: 10px;
  background: #fff;
  color: #3d4f63;
  display: grid;
  place-items: center;
  cursor: pointer;
  transition: all 0.15s ease;

  .mdi {
    font-size: 18px;
  }

  &:hover {
    border-color: var(--accent);
    color: var(--accent);
    background: #eef4fa;
  }

  &--waste {
    color: #b42318;

    &:hover {
      border-color: #f0b4ae;
      background: #fff5f4;
      color: #912018;
    }
  }

  &--accent {
    background: var(--accent);
    border-color: var(--accent);
    color: #fff;

    &:hover {
      background: #093f6d;
      border-color: #093f6d;
      color: #fff;
    }
  }
}

.stock-card__meta {
  display: grid;
  gap: 8px;

  div {
    display: flex;
    justify-content: space-between;
    gap: 12px;
  }

  span {
    color: var(--muted);
    font-size: 12px;
  }

  strong {
    font-size: 14px;
  }
}

.stock-card__qty {
  color: var(--accent);
}

.stock-page__empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-height: 240px;
  padding: 40px 20px;
  border: 1px dashed var(--line);
  border-radius: 16px;
  background: #fff;
  color: var(--muted);
  text-align: center;

  .mdi {
    font-size: 40px;
    color: #9eb6cc;
  }

  p {
    margin: 0;
    color: var(--ink);
    font-size: 16px;
    font-weight: 700;
  }

  span {
    font-size: 13px;
  }
}

.stock-page__pagination {
  display: flex;
  justify-content: center;
  margin-top: 28px;
}

.stock-page__dialog {
  border-radius: 16px !important;
}

.stock-history {
  margin-bottom: 14px;
  padding-bottom: 12px;
  border-bottom: 1px solid #eef2f6;

  p {
    margin: 4px 0;
    font-size: 14px;
  }
}

.stock-history__date {
  font-weight: 700 !important;
  color: var(--accent);
}

.stock-history__waste {
  color: #912018;

  b {
    color: #b42318;
  }
}

.stock-history__empty {
  padding: 24px 0;
  text-align: center;
  color: var(--muted);
}

.stock-waste__hint {
  margin: 0 0 14px;
  padding: 10px 12px;
  border-radius: 10px;
  background: #fff5f4;
  color: #912018;
  font-size: 13px;
}

.stock-waste__sum {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 12px;
  margin-top: 8px;
  padding: 12px 14px;
  border-radius: 12px;
  background: #f8fafc;
  border: 1px solid #e5edf5;

  span {
    color: var(--muted);
    font-size: 13px;
    font-weight: 650;
  }

  strong {
    font-size: 18px;
    color: #b42318;
  }
}

@media (max-width: 720px) {
  .stock-page {
    padding: 20px 16px 32px;
  }

  .stock-page__header {
    flex-direction: column;
  }

  .stock-page__search {
    width: 100%;
  }
}
</style>
